import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import type { Dependency } from '../common/types';
import type { State } from './types';
import { fromDraft, getUpdateBody, toDraft } from './utils';

const updateDraftDependency =
	(id: number, updater: (arg0: Dependency) => Dependency | undefined): Action<State> =>
	({ getState, setState }) => {
		const { draft } = getState();
		const updated = draft.reduce<typeof draft>((acc, elem) => {
			const next = elem.issueLinkTypeId === id ? updater(elem) : elem;
			return next === undefined ? acc : acc.concat(next);
		}, []);

		setState({ draft: updated });
	};

export const actions = {
	swapDirection:
		(id: number): Action<State> =>
		(api) => {
			const swap = (direction: Dependency['direction']): Dependency['direction'] => {
				switch (direction) {
					case 'INWARD':
						return 'OUTWARD';

					case 'OUTWARD':
						return 'INWARD';

					default:
						throw new Error('illegal state');
				}
			};

			updateDraftDependency(id, ({ issueLinkTypeId, direction }) => ({
				issueLinkTypeId,
				direction: swap(direction),
			}))(api);
		},

	changeIssueLinkTypeId:
		(from: number, to: number | undefined): Action<State> =>
		(api) => {
			updateDraftDependency(from, ({ direction }) =>
				to === undefined
					? undefined
					: {
							direction,
							issueLinkTypeId: to,
						},
			)(api);
		},

	add:
		(id: number, direction: Dependency['direction']): Action<State> =>
		({ setState, getState }) => {
			setState({
				draft: getState().draft.concat([
					{
						direction,
						issueLinkTypeId: id,
					},
				]),
			});
		},

	reset:
		(): Action<State> =>
		({ getState, setState }) => {
			const { dependencies, dependencyIds } = getState();
			setState({ draft: toDraft(dependencyIds, dependencies) });
		},

	save:
		(): Action<State> =>
		async ({ getState, setState }) => {
			const { draft } = getState();

			setState({ savingInProgress: true });
			await new Promise((resolve) => setTimeout(resolve, 2000));

			await performPostRequest('/rest/jpo/1.0/dependencySettings', {
				body: JSON.stringify(getUpdateBody(draft)),
			});

			setState({ savingInProgress: false });
			setState(fromDraft(draft));
		},
};
